import { CheckCircle, Calendar, Shirt, Mail, Home, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const nextSteps = [
  {
    icon: Mail,
    title: "Check Your Email",
    description: "We'll send you a confirmation email with all the details.",
  },
  {
    icon: Calendar,
    title: "We'll Confirm Your Dates",
    description: "Our team will be in touch to confirm your chosen holiday dates.",
  },
  {
    icon: Shirt,
    title: "Get Ready!",
    description: "Check the 'What to Wear' section so you're prepared for day one.",
  },
];

const reminders = [
  "T-shirt",
  "Tracksuit bottoms or leggings (no shorts)",
  "Clean trainers",
  "Water bottle",
  "Nut-free packed lunch",
];

export default function ThankYouPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A1A2F] flex items-center justify-center px-4 py-12">
      <div className="max-w-2xl w-full">
        {/* Success Card */}
        <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-[#D4A857] p-8 text-center text-[#0A1A2F] relative overflow-hidden">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <CheckCircle className="w-12 h-12 text-[#D4A857]" />
            </div>
            
            <h1 className="text-3xl sm:text-4xl font-extrabold mb-2 font-['Montserrat']">
              Booking Received!
            </h1>
            <p className="text-[#0A1A2F]/80 text-lg font-['Lato']">
              You're all set for an amazing fencing adventure!
            </p>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Masky Celebration - Medium size */}
            <div className="text-center mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_1abea0ec-cded-4678-a29f-de28c813ea65/artifacts/2pfnnew9_bigger%20masky.png"
                alt="Masky celebrating"
                className="w-44 mx-auto mb-4"
              />
              <div className="bg-[#F4F4F4] rounded-lg p-4 inline-block">
                <p className="text-[#0A1A2F] font-bold text-lg font-['Montserrat']">
                  "Hooray! I can't wait to meet your young fencer!"
                </p>
                <p className="text-gray-500 text-sm mt-1 font-['Lato']">— Masky</p>
              </div>
            </div>

            {/* What Happens Next */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-[#0A1A2F] mb-4 text-center font-['Montserrat']">
                What Happens Next?
              </h2>
              <div className="space-y-4">
                {nextSteps.map((step, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 bg-[#F4F4F4] rounded-lg p-4"
                  >
                    <div className="w-10 h-10 bg-[#D4A857] rounded-lg flex items-center justify-center flex-shrink-0">
                      <step.icon className="w-5 h-5 text-[#0A1A2F]" />
                    </div>
                    <div>
                      <p className="font-bold text-[#0A1A2F] font-['Montserrat']">{step.title}</p>
                      <p className="text-gray-600 text-sm font-['Lato']">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Reminder: What to Bring */}
            <div className="bg-[#0A1A2F] rounded-lg p-6 text-white mb-8">
              <h3 className="font-bold text-[#D4A857] mb-3 flex items-center gap-2 font-['Montserrat'] uppercase text-sm">
                <Shirt className="w-5 h-5" />
                Remember to Bring:
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {reminders.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm font-['Lato']">
                    <span className="text-[#D4A857]">✓</span>
                    <span className="text-white/90">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-[#D4A857] text-xs mt-3 font-medium font-['Lato']">
                ⚠️ All lunches must be nut-free
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-3">
              <Button
                onClick={() => navigate("/")}
                className="w-full bg-[#0A1A2F] hover:bg-[#0D2240] text-white font-bold py-4 rounded transition-all font-['Montserrat'] uppercase tracking-wide"
              >
                <Home className="w-5 h-5 mr-2" />
                Back to Homepage
              </Button>
              
              <p className="text-center text-gray-500 text-sm font-['Lato']">
                Questions? Contact us at{" "}
                <a
                  href="mailto:admin@ukswordfencing.com"
                  className="text-[#D4A857] font-medium hover:underline"
                >
                  admin@ukswordfencing.com
                </a>
                {" "}or call{" "}
                <a
                  href="tel:+442036375707"
                  className="text-[#D4A857] font-medium hover:underline"
                >
                  020 3637 5707
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <p className="text-center text-white/60 text-sm mt-6 font-['Lato']">
          Fighting Fit Fencing • Ofsted Registered
        </p>
      </div>
    </div>
  );
}
